import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useBookingCompletionSegment } from 'hooks/use-booking-completion-segment.hook';
import {
  getTransactions,
  resetCurrentTransaction,
  resetPatchTransactionStatus,
  resetRedirectTransaction,
  resetTransaction,
} from 'store/transactions/actions';
import { useLocation } from 'react-router';
import { LANDING_PATH, PATCH_STATUSES, QUERY_PARAMS } from 'shared/consts';
import { getBookingDetailsPath } from 'shared/consts/paths';
import qs from 'qs';
import { pick } from 'utils/pickObjectProperty';
import { goBack, push, replace } from 'store/router/actions';
import { useSearchParams } from '../use-search-params.hook';
import { searchParams } from 'pages/date-select-page/interface';
import { resetCart, resetSubmitCartStatus } from 'store/cart/actions';
import { selectTypeOfPatchTransaction } from 'store/transactions/selectors';
import { updateInitialRoute } from 'store/routes/actions';
import { useMiniappSdk } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';

interface UseReceiptScreenReturnValues {
  isCancelBookingStep: boolean;
  setIsCancelBookingStep: React.Dispatch<React.SetStateAction<boolean>>;
  onCloseReceipt: VoidFunction;
  onPressBack: VoidFunction;
  openReceipt: (transactionId?: number | string) => void;
}

export const useReceiptScreen = (): UseReceiptScreenReturnValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [isCancelBookingStep, setIsCancelBookingStep] = useState<boolean>(false);
  const { shouldOpenCancelStep, closeAfterBooking, ...restQueryParams } = useSearchParams<searchParams>();
  const typeOfPatchTransaction = useSelector(selectTypeOfPatchTransaction);
  const client = useMiniappSdk();
  const { enable2WayChatRedesignResourceBooking } = useFlags();
  const completionSegment = useBookingCompletionSegment();

  const onCloseReceipt = useCallback(() => {
    if (closeAfterBooking === 'true') {
      client?.close();
      return;
    }

    if (pathname === LANDING_PATH || pathname.includes('bookings')) {
      dispatch(goBack());
    } else {
      if (typeOfPatchTransaction === PATCH_STATUSES.CANCELLED) {
        dispatch(getTransactions.request({}));
      }
      const queryString = qs.stringify(
        pick(
          restQueryParams,
          QUERY_PARAMS.filter(
            param =>
              param !== 'startTime' &&
              param !== 'startDate' &&
              param !== 'endTime' &&
              param !== 'isExact' &&
              param !== 'duration' &&
              param !== 'presetWindows' &&
              param !== 'startDates' &&
              param !== 'capacity',
          ),
        ),
      );

      dispatch(updateInitialRoute(LANDING_PATH));
      dispatch(replace(`/?${queryString}`));
    }
    dispatch(resetPatchTransactionStatus());
    dispatch(resetTransaction());
    dispatch(resetRedirectTransaction());
    dispatch(resetSubmitCartStatus());
    dispatch(resetCart());
    dispatch(getTransactions.request({}));
  }, [dispatch, pathname, restQueryParams, typeOfPatchTransaction, closeAfterBooking, client]);

  const openReceipt = useCallback(
    (transactionId?: number | string) => {
      dispatch(resetCurrentTransaction());

      // RES-999: de-modal booking details — behind the flag, navigate straight to the routed
      // page instead of the nested /receipt sub-route that ReceiptBooking wraps in a SwipeModal
      // (or the plain-Container non-modal fallback). Only takes this branch when we actually have
      // an id to route with; falls through to the existing behavior otherwise (e.g. from checkout).
      if (enable2WayChatRedesignResourceBooking && transactionId !== undefined && transactionId !== null) {
        dispatch(push(getBookingDetailsPath(transactionId)));
        return;
      }

      /*
       * RES-1407: with no id this is a finished checkout (`Checkout` calls it bare), and under the flag
       * that goes to `booked` -- the add-on offer or My bookings, never the old receipt.
       */
      const queryString = qs.stringify({ ...restQueryParams, closeAfterBooking });
      if (pathname === LANDING_PATH) {
        dispatch(push(`${completionSegment}?${queryString}`));
      } else if (pathname.includes('bookings')) {
        dispatch(push(`${pathname}/${completionSegment}?${queryString}`));
      } else {
        dispatch(replace(`${pathname.replace('checkout', completionSegment)}?${queryString}`));
      }
    },
    [dispatch, pathname, restQueryParams, closeAfterBooking, enable2WayChatRedesignResourceBooking, completionSegment],
  );

  const onPressBack = useCallback(() => {
    if (shouldOpenCancelStep) {
      dispatch(goBack());
    } else {
      setIsCancelBookingStep(false);
    }
  }, [dispatch, shouldOpenCancelStep]);

  return {
    isCancelBookingStep,
    setIsCancelBookingStep,
    onCloseReceipt,
    onPressBack,
    openReceipt,
  };
};
